import ENV from 'api/env';
import { formatCurrencyString } from 'helpers/utils';
import { CbPromoCodeKind } from 'api/graphql-global-types';
import { GetCbPromoCodes_getCbPromoCodes_entities } from 'api/cbPromocodes/types/GetCbPromoCodes';
import {
  CB_DISCOUNT_DURATION_LABELS,
  CB_PROMO_CODE_KIND_LABELS,
} from './constants';

export type CbPromoCode = GetCbPromoCodes_getCbPromoCodes_entities;

/**
 * The brand app resolves a shared promo link via getCbPromoCodeOffer and
 * routes by kind. The backend stores only the code; the backoffice builds the
 * shareable URL from it. CAMPAIGN_APP_URL already carries a trailing slash in
 * staging/prod (not in dev), so normalise before appending.
 */
export const buildPromoCodeShareLink = (code: string): string => {
  const base = ENV.CAMPAIGN_APP_URL.replace(/\/$/, '');

  return `${base}/promo/${code}`;
};

export const getPromoCodeKindLabel = (kind: CbPromoCodeKind): string =>
  CB_PROMO_CODE_KIND_LABELS[kind] || kind;

/**
 * Human-readable summary of a code's economics, told apart by __typename since
 * CbPromoCode is a GraphQL interface with one concrete type per kind.
 */
export const getPromoCodeValueLabel = (record: CbPromoCode): string => {
  switch (record.__typename) {
    case 'CbCreditFreePromoCode':
      return formatCurrencyString(record.creditAmount);
    case 'CbCreditMatchPromoCode': {
      const cap = record.maxPromoPerRedemption
        ? ` (max ${formatCurrencyString(record.maxPromoPerRedemption)})`
        : '';

      return `${record.matchPercent}% match${cap}`;
    }
    case 'CbSubscriptionDiscountPromoCode': {
      const duration = CB_DISCOUNT_DURATION_LABELS[record.discountDuration];
      const months =
        record.discountDurationMonths != null
          ? ` · ${record.discountDurationMonths} mo`
          : '';

      return `${record.percentOff}% off · ${duration}${months}`;
    }
    case 'CbSubscriptionTrialPromoCode':
      return `${record.trialDays}-day trial`;
    default:
      return '-';
  }
};

/**
 * Per-kind minted/forgone analytics for the list + detail views.
 */
export const getPromoCodeAnalyticsLabel = (record: CbPromoCode): string => {
  switch (record.__typename) {
    case 'CbCreditFreePromoCode':
    case 'CbCreditMatchPromoCode':
      return `${formatCurrencyString(record.promoIssuedTotal)} issued`;
    case 'CbSubscriptionDiscountPromoCode':
      return `${formatCurrencyString(record.discountGrantedTotal)} forgone`;
    case 'CbSubscriptionTrialPromoCode':
      return `${record.trialMetrics.trialsConverted}/${record.trialMetrics.trialsStarted} converted`;
    default:
      return '-';
  }
};

export const formatRemainingUses = (record: CbPromoCode): string => {
  const usedCount = record.usedCount ?? 0;

  if (record.remainingUses == null) {
    return `${usedCount} used · ∞`;
  }

  return `${usedCount} used · ${record.remainingUses} left`;
};
